<template>
  <div>
    <div class="panel-heading order-heading-with-stats">
      <h4 class="panel-title">{{ $t('orders.allOrdersTitle') }}</h4>

      <div class="order-heading-right">
        <div class="order-toolbar-buttons">
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="toggleFiltersVisible">
            <i class="material-icons">{{ showFilters ? 'expand_less' : 'expand_more' }}</i>
            <span v-if="showButtonLabels">{{ $t('orders.filtersToggleLabel') }}</span>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="reloadTable">
            <i class="material-icons">cached</i>
            <span v-if="showButtonLabels">{{ $t('datatable.reloadShortLabel') }}</span>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="sendToExport">
            <i class="material-icons">explicit</i>
            <span v-if="showButtonLabels">{{ $t('datatable.exportButton') }}</span>
          </a>
          <a v-if="$_has_perm('purchase_generate_qrcode')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openGenerateQrCodeModal()">
            <i class="fa fa-qrcode"></i>
            <span v-if="showButtonLabels">{{ $t('datatable.qrCodeShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('add_shipping_invoice')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openOrderInvoiceFromHeader()">
            <i class="fa fa-file-text-o"></i>
            <span v-if="showButtonLabels">{{ $t('datatable.invoiceShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('change_order_status')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openBulkChangeStatusDialog()">
            <i class="material-icons" style="color:#6a1b9a">autorenew</i>
            <span v-if="showButtonLabels">{{ $t('datatable.statusShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('all_order_view_customer_summary')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openCustomerDeliverySummaryModal()">
            <i class="material-icons" style="color:#00897b">people</i>
            <span v-if="showButtonLabels">{{ $t('datatable.summaryShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('unclear_order')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="confirmUnclearAll()">
            <i class="material-icons" style="color:#c62828">undo</i>
            <span v-if="showButtonLabels">{{ $t('datatable.unclearButton') }}</span>
          </a>
          <a class="order-toolbar-display-toggle" @click="$_toggle_toolbar_button_display()">
            <i class="material-icons">{{ showButtonLabels ? 'label_off' : 'label' }}</i>
          </a>
        </div>

        <div class="order-stats-row order-stats-row--compact">
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--blue"><i class="material-icons">list_alt</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('reports.totalOrders') }}</span>
              <span class="order-stat-value">{{ summaryTotalOrders }}</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--green"><i class="material-icons">attach_money</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('printLogs.totalAmountLabel') }}</span>
              <span class="order-stat-value">$ {{ summaryTotalAmount.toFixed(2) }}</span>
              <span v-if="qrCodeValues.length > 0" class="order-stat-note">({{ qrCodeValues.length }} selected)</span>
              <span v-else-if="!filterMade" class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--purple"><i class="material-icons">today</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('adminDashboard.ordersTodayLabel') }}</span>
              <span class="order-stat-value">{{ ordersTodayCount }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--orange"><i class="material-icons">payment</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('adminDashboard.pendingPaymentsLabel') }}</span>
              <span class="order-stat-value">{{ pendingPaymentsCount }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="panel-body"
      style="display: block;"
    >
      <div class="row">
        <!-- Attach to Delivery button — commented out for now, kept for later use.
        :showAttachToDeliveryButton="true"
        :attachToDelivery="attachSelectedToDelivery"
        -->
        <preDatatableComponent
          ref="preDatatable"
          :permissions="{
            generate_qr_code: 'purchase_generate_qrcode',
            send_to_warehouse: 'send_to_warehouse',
            change_status_permission: 'change_order_status',
            view_customer_delivery_summary: 'all_order_view_customer_summary',
            unclear_permission: 'unclear_order',
            add_shipping_invoice: 'add_shipping_invoice'
            // attach_to_delivery_permission: 'attach_order_to_delivery' — Attach to Delivery button commented out for now, kept for later use
          }"
          :removeAddButton="true"
          :removeWarehouseDeliverButton="true"
          :hideReloadButton="true"
          :collapsibleFilters="true"
          :filtersVisible="showFilters"
          :tableData="tableData"
          :openGenerateQrCodeModal="openGenerateQrCodeModal"
          :openOrderInvoiceModal="openOrderInvoiceFromHeader"
          :sendToExport="sendToExport"
          :confirmChangeStatusAll="openBulkChangeStatusDialog"
          :openCustomerDeliverySummaryModal="openCustomerDeliverySummaryModal"
          :confirmUnclearAll="confirmUnclearAll"
          :resetGetResults="resetGetResults"
          :searchMe="searchMe"
          :enableCustomerSearchSuggestions="true"
          :pagination="pagination"
          :perPageFix="perPageFix"
          :filterTable="filterTable"
          :resetGetResults2="resetGetResults2"
          :columns="columns"
          :showHideColumn="showHideColumn"
          :storageKey="'cols_allOrder_v3'"
          :defaultHideCol="defaultHideCol"
          :resultsVersion="resultsVersion"
          :filterMade="filterMade"
          :totalResult="totalResult"
          :platformFilterItems="platformFilterItems"
          :platformFilterChange="handlePlatformFilter"
          @branch-edit="branchEdit"
        >
          <template slot="custom-filter-start">
            <template v-if="showFilters">
            <v-flex class="filter-line-break"></v-flex>
            <v-flex class="filter-field px-2">
              <v-select
                :items="warehousesWithSelectAll"
                v-model="warehouseFilterVal"
                :label="$t('columns.warehouse')"
                multiple
                clearable
                hide-details
                single-line
                item-text="text"
                item-value="value"
              >
                <template slot="selection" slot-scope="{ index }">
                  <span v-if="index === 0">{{ warehouseFilterVal.length }} {{ $t('columns.selectedSuffix') }}</span>
                </template>
              </v-select>
            </v-flex>
            <v-flex class="filter-field px-2">
              <v-select
                :items="statusesWithSelectAll"
                v-model="statusFilterVal"
                :label="$t('columns.status')"
                multiple
                clearable
                hide-details
                single-line
                item-text="text"
                item-value="value"
              >
                <template slot="selection" slot-scope="{ index }">
                  <span v-if="index === 0">{{ statusFilterVal.length }} {{ $t('columns.selectedSuffix') }}</span>
                </template>
              </v-select>
            </v-flex>
            <v-flex class="filter-field px-2">
              <v-select
                :items="paymentFilterItemsWithSelectAll"
                v-model="paymentFilterVal"
                :label="$t('columns.payment')"
                multiple
                clearable
                hide-details
                single-line
                item-text="text"
                item-value="value"
              >
                <template slot="selection" slot-scope="{ index }">
                  <span v-if="index === 0">{{ paymentFilterVal.length }} {{ $t('columns.selectedSuffix') }}</span>
                </template>
              </v-select>
            </v-flex>
            <v-flex class="filter-field px-2">
              <v-select
                :items="officeStatusFilterItems"
                v-model="officeStatusFilterVal"
                :label="$t('columns.outOffice')"
                multiple
                clearable
                hide-details
                single-line
                item-text="text"
                item-value="value"
              >
                <template slot="selection" slot-scope="{ index }">
                  <span v-if="index === 0">{{ officeStatusFilterVal.length }} {{ $t('columns.selectedSuffix') }}</span>
                </template>
              </v-select>
            </v-flex>
            <v-flex class="filter-field px-2">
              <v-select
                :items="clearedFilterItems"
                v-model="clearedFilterVal"
                :label="$t('columns.cleared')"
                hide-details
                single-line
                item-text="text"
                item-value="value"
              ></v-select>
            </v-flex>
            <v-flex class="filter-field px-2">
              <v-select
                :items="dateTypeItems"
                :value="dateTypeFilter"
                @input="handleDateTypeFilter"
                :label="$t('orders.dateTypeLabel')"
                hide-details
                single-line
                item-text="text"
                item-value="value"
              ></v-select>
            </v-flex>
            <v-flex class="filter-field px-2">
              <v-select
                :items="orderSortDirItems"
                :value="tableData.sortDir"
                @input="handleOrderSortDirFilter"
                :label="$t('orders.orderSort')"
                hide-details
                single-line
                item-text="text"
                item-value="value"
              ></v-select>
            </v-flex>
            </template>
          </template>
        </preDatatableComponent>

        <div class="col-xs-12">
          <div class="row text-center">
            <span
              id="test-editable"
              style="opacity:0"
            ></span>
          </div>

          <div class="table-responsive oc-table-shell">
          <datatable
            v-if="!isMobile"
            :sortDir="tableData.sortDir"
            :columns="columns"
            :sortByColumn="sortByColumn"
            :searchOneCol="searchOneCol"
            :sortKey="sortKey"
            :sortOrders="sortOrders"
            :checkAll="checkAll"
            :unCheckAll="unCheckAll"
          >
            <tbody>
              <tr
                v-for="(r, index) in apiResults"
                :key="r.order_id"
                :style="qrCodeValues.find(x => x[0] == r.order_id) ? 'background-color: #FFF9C4' : '' "
              >
                <td>
                  <input
                    :id="'cb' + index"
                    type="checkbox"
                    :value="[r.order_id, r.full_name, r.phone_number, r.user_id, r.theOrderID, r.is_ship_only, r.product_name, r.warehouse_id_fk, r.transportation_type, r.is_ship_only, r.total_amount, r.size, r.payment_status, r.order_invoice_number]"
                    v-model="qrCodeValues"
                  />
                </td>
                <td class="oc-cell hover-container">
                  <div class="oc-order-number">#{{ r.theOrderID }}</div>
                  <div class="oc-order-sub" v-if="r.qrcode"><i class="fa fa-qrcode" aria-hidden="true"></i> {{ r.qrcode }}</div>
                  <div class="oc-order-sub"><view-order-users :orderData="r" /></div>
                </td>
                <td class="oc-cell ws-nowrap">
                  <customer-device-icon :orderSource="r.order_source" />
                  <img v-if="r.profile_image" :src="r.profile_image && r.profile_image.startsWith('http') ? r.profile_image : `${$_image_path()}${r.profile_image}`" class="customer-profile-image" />
                  <span class="customer-name-stack">
                    <span v-if="r.full_name && r.full_name.trim().split(/\s+/).length > 3" style="word-break: break-word; white-space: pre-line;">
                      {{ r.full_name.replace(/((?:\S+\s+){3})/g, '$1\n') }}
                    </span>
                    <span v-else>
                      {{ r.full_name }}
                    </span>
                    <span v-if="r.customer_id_fk" class="oc-customer-id">ID: {{ r.customer_id_fk }}</span>
                    <span v-if="r.phone_number" class="customer-phone-line">{{ r.phone_number }}</span>
                    <span v-if="r.phone_number_2" class="customer-phone-line">{{ r.phone_number_2 }}</span>
                  </span>
                  <open-whatsapp-and-viber :phoneNumber="r.phone_number" />
                </td>
                <td class="oc-cell">
                  <div class="oc-item-name">{{
                    r.product_name.substr(0, 35) +
                      (r.product_name.length > 35 ? '...' : '')
                  }}</div>
                  <div class="oc-item-sub" v-if="r.brand">{{ $t('columns.brand') }}: <strong>{{ r.brand }}</strong></div>
                  <a v-if="r.product_url" :href="r.product_url" target="_blank" rel="noopener noreferrer" class="oc-item-link">
                    <i class="material-icons">link</i> {{ $t('columns.url') }}
                  </a>
                </td>
                <td class="oc-cell oc-product-cell">
                  <img
                    v-if="r.image"
                    :src="r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`"
                    class="oc-thumb"
                    @click="lightboxImage = r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`; lightboxOpen = true"
                  />
                  <span v-else class="oc-thumb-placeholder">—</span>
                </td>
                <td
                  class="oc-cell"
                  @click="$_has_perm('change_order_status') && !isStatusChangeLocked(r) && openChangeStatusDialog(r)"
                  :style="($_has_perm('change_order_status') && !isStatusChangeLocked(r)) ? 'cursor:pointer;' : ''"
                  :title="isStatusChangeLocked(r) ? $t('changeOrderStatus.statusLockedOrderCanceled') : ($_has_perm('change_order_status') ? $t('changeOrderStatus.clickToChangeStatus') : '')"
                >
                  <!-- Hiding "Archived" for now when the order is really a canceled/refunded order the
                  customer archived from their own list (myOrders.vue archiveOrder) — it duplicated the
                  canceled/refunded chip below. -->
                  <span
                    v-if="!(r.order_status === 'archived' && (r.is_canceled == '1' || r.is_refunded == '1'))"
                    class="oc-status-chip"
                    :style="{ backgroundColor: statusColor(r.order_status).bg, color: statusColor(r.order_status).color }"
                  >{{ statusLabel(r.order_status) }}</span>
                  <span
                    v-if="r.order_status === 'archived' && r.is_canceled == '1'"
                    class="oc-status-chip rtl-ml-2"
                    style="background-color:#FFF3E0; color:#E65100"
                  >canceled</span>
                  <span
                    v-if="r.order_status === 'archived' && r.is_refunded == '1'"
                    class="oc-status-chip rtl-ml-2"
                    style="background-color:#FFEBEE; color:#B71C1C"
                  >refunded</span>
                  <span
                    v-if="r.cleared_at"
                    class="oc-status-chip rtl-ml-2"
                    style="background-color:#FFF9C4; color:#7A6A00"
                  >cleared</span>
                  <div class="oc-location-line" v-if="r.country">
                    <i class="material-icons oc-location-icon">place</i>{{ r.country }}<span v-if="r.transportation_type_obj"> · {{ r.transportation_type_obj.name.replace('Transportation', '') }}</span>
                  </div>
                </td>
                <td class="combined-cell-td oc-cell">
                  <div class="combined-cell">
                    <div><span class="combined-cell-label">{{ $t('columns.price') }}:</span> {{ r.item_price ? '$' + r.item_price : '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.shipping') }}:</span> {{ r.internal_shipping_handling ? '$' + r.internal_shipping_handling : '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.total') }}:</span> {{ r.total_amount ? '$' + r.total_amount : '—' }}</div>
                    <div class="combined-cell-divider"></div>
                    <div><span class="combined-cell-label">{{ $t('columns.qty') }}:</span> {{ r.quantity }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.size') }}:</span> {{ r.size || '—' }}</div>
                  </div>
                </td>
                <td
                  class="oc-cell hover-container"
                  style="min-width: 150px"
                >
                  <view-order-dates :orderData="r" stack-time />
                  <span
                    v-if="r.payment_status"
                    class="oc-payment-chip"
                    :style="{ background: paymentStatusColor(r.payment_status, r.invoice_edit_count).bg, color: paymentStatusColor(r.payment_status, r.invoice_edit_count).color }"
                  >{{ paymentStatusLabel(r.payment_status) }}</span>
                  <div v-if="r.payment_status">
                    <span
                      class="oc-payment-chip"
                      :style="{ background: officeStatusColor(r.invoice_office_status).bg, color: officeStatusColor(r.invoice_office_status).color }"
                    >{{ officeStatusLabel(r.invoice_office_status) }}</span>
                  </div>
                </td>
                <td class="oc-cell oc-actions-cell">
                  <div class="oc-actions-grid">
                  <a
                    v-if="$_has_perm('view_order_detail')"
                    class="oc-action-btn"
                    :tooltip="$t('orders.viewDetails')"
                    @click="initDetail('modal_showApp', r.order_id)"
                  >
                    <i class="material-icons" style="color:#01579B">aspect_ratio</i>
                  </a>
                  <a
                    v-if="$_has_perm('view_order_shipping_invoice') && r.order_invoice_id_fk"
                    class="oc-action-btn"
                    :tooltip="$t('orders.viewShippingInvoiceTooltip')"
                    @click="viewOrderShippingInvoice(r)"
                  >
                    <i class="material-icons" style="color:#00695C">receipt</i>
                  </a>
                  <a
                    v-if="$_has_perm('purchase_generate_qrcode')"
                    class="oc-action-btn"
                    :tooltip="$t('orders.generateQrCodeTooltip')"
                    @click="openQrCodeForRow(r)"
                  >
                    <i class="fa fa-qrcode" style="color:#6a1b9a"></i>
                  </a>
                  <a
                    v-if="($_has_perm('cancel_new_order') || $_has_perm('purchase_cancel_order') || $_has_perm('cancel_approved_order') || $_has_perm('cancel_damage_order') || $_has_perm('cancel_return_order')) && (r.order_status === 'new_order' || r.order_status === 'processed_order' || r.order_status === 'approved_order' || r.order_status === 'pending_answer' || r.order_status === 'damage' || r.order_status === 'return' || refundOnCancelStatuses.includes(r.order_status))"
                    class="oc-action-btn"
                    :tooltip="$t('customer.cancelOrderTitle')"
                    @click="openCancelingOrderModal(r)"
                  >
                    <i class="material-icons" style="color:#e53935">cancel</i>
                  </a>
                  <a
                    v-if="($_has_perm('process_new_order') || $_has_perm('edit_charged_order')) && (r.order_status === 'new_order' || r.order_status === 'purchased' || r.order_status === 'branch_delivery' || r.order_status === 'pending_answer' || r.order_status === 'int_shipment')"
                    class="oc-action-btn"
                    :tooltip="$t('orders.processOrderTooltip')"
                    @click="openProcessOrderModal(r)"
                  >
                    <i class="material-icons" style="color:#424242">settings</i>
                  </a>
                  <a
                    v-if="$_has_perm('generate_order_invoice') && (r.order_status === 'pending_to_branch' || r.order_status === 'branch_delivery' || r.order_status === 'delivered' || r.order_status === 'archived')"
                    class="oc-action-btn"
                    :tooltip="$t('orders.generateInvoiceTooltip')"
                    @click="openOrderInvoiceModal(r)"
                  >
                    <i class="material-icons" style="color:#2e7d32">receipt</i>
                  </a>
                  <a
                    v-if="$_has_perm('move_to_damage')"
                    class="oc-action-btn"
                    :tooltip="$t('orders.moveToDamageTooltip')"
                    @click="moveToDamage(r.order_id)"
                  >
                    <i class="material-icons" style="color:#D32F2F">broken_image</i>
                  </a>
                  <a
                    v-if="$_has_perm('move_to_return')"
                    class="oc-action-btn"
                    :tooltip="$t('orders.moveToReturnTooltip')"
                    @click="moveToReturn(r.order_id)"
                  >
                    <i class="material-icons" style="color:#FF9800">assignment_return</i>
                  </a>
                  </div>
                </td>
              </tr>
              <tr id="no-result-row">
                <td
                  :colspan="columns.length"
                  class="text-center"
                >
                  <v-alert
                    type="error"
                    :value="alertValue"
                  >
                    {{ $t('common.noResultsFound') }}
                  </v-alert>
                </td>
              </tr>
            </tbody>
          </datatable>
          </div>

          <mobile-datatable
            :isOrder="true"
            :isPurchase="true"
            :checkAll="checkAll"
            :unCheckAll="unCheckAll"
            v-if="isMobile"
          >
            <tbody>
              <tr
                v-for="(r, index) in apiResults"
                :key="r.order_id"
                :style="qrCodeValues.find(x => x[0] == r.order_id) ? 'background-color: #FFF9C4' : ''"
              >
                <td>
                  <input
                    :id="'cb' + index"
                    type="checkbox"
                    :value="[r.order_id, r.full_name, r.phone_number, r.user_id, r.theOrderID, r.is_ship_only, r.product_name, r.warehouse_id_fk, r.transportation_type, r.is_ship_only, r.total_amount, r.size, r.payment_status, r.order_invoice_number]"
                    v-model="qrCodeValues"
                  />
                </td>
                <td>
                  {{ r.theOrderID }} , <br />
                  {{ r.product_name }} , <br />
                  <img
                    v-if="r.image"
                    :src="r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`"
                    style="width:90px; height:90px; object-fit:cover; border-radius:4px; cursor:pointer; margin:4px 0;"
                    @click="lightboxImage = r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`; lightboxOpen = true"
                  />
                  <br v-if="r.image" />
                  <a
                    :href="r.product_url"
                    target="_blank"
                  >{{
                    r.product_url.replace(/http(s)?:\/\//, '').substr(0, 10) +
                      (r.product_url.length > 10 ? '...' : '')
                  }}</a>
                  <br />
                  {{ r.country }} ,
                  <br />
                  {{ $t('columns.purchaseNote') }}: {{ r.purchase_note }}
                  <br />
                  {{ $t('columns.actualCost') }}: {{ r.actual_price }}
                  <br />
                  {{ $t('columns.quantity') }}: {{ r.quantity }}
                  <br />
                  <span v-if="r.payment_status">
                    {{ $t('columns.payment') }}:
                    <span
                      :style="{ background: paymentStatusColor(r.payment_status, r.invoice_edit_count).bg, color: paymentStatusColor(r.payment_status, r.invoice_edit_count).color, padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }"
                    >{{ paymentStatusLabel(r.payment_status) }}</span>
                    <br />
                    <span
                      :style="{ background: officeStatusColor(r.invoice_office_status).bg, color: officeStatusColor(r.invoice_office_status).color, padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }"
                    >{{ officeStatusLabel(r.invoice_office_status) }}</span>
                  </span>
                </td>
                <td>
                  <span v-if="r.order_status === 'new_order'">
                    <span class="label bg-primary-600">
                      {{ r.order_status.replace('_', ' ') }}</span>
                  </span>
                  <span v-if="r.order_status === 'processed_order'">
                    <span class="label bg-indigo-600"> {{ r.order_status.replace('_', ' ') }}</span>
                  </span>
                  <span v-if="r.order_status === 'canceled'">
                    <span class="label bg-orange-600"> {{ r.order_status }}</span>
                  </span>
                  <span v-if="r.order_status === 'refunded'">
                    <span class="label bg-danger-600"> {{ r.order_status }}</span>
                  </span>
                  <span v-if="r.order_status === 'approved_order'">
                    <span class="label bg-green-600"> {{ r.order_status.replace('_', ' ') }}</span>
                  </span>
                  <span v-if="r.order_status === 'purchased'">
                    <span class="label bg-teal-400"> {{ r.order_status }}</span>
                  </span>
                  <span v-if="r.order_status === 'warehouse_delivered_order'">
                    <span class="label bg-purple-400">
                      {{ r.order_status.replace('_', ' ').replace('_order', '') }}</span>
                  </span>
                  <span v-if="r.order_status === 'int_shipment'">
                    <span class="label bg-info-400"> {{ r.order_status.replace('_', ' ') }}</span>
                  </span>
                  <span v-if="r.order_status === 'branch_delivery'">
                    <span class="label bg-violet-600"> {{ r.order_status.replace('_', ' ') }}</span>
                  </span>
                  <span v-if="r.order_status === 'delivered'">
                    <span class="label bg-warning-300">
                      {{ r.order_status.replace('_', ' ') }}</span>
                  </span>
                  <span v-if="r.order_status === 'archived'">
                    <span v-if="r.is_canceled == '1'">
                      <span class="label bg-orange-600">canceled</span>
                    </span>
                    <span
                      v-else
                      class="label bg-warning-300"
                    >
                      {{ r.order_status.replace('archived', 'delivered') }}</span>
                  </span>
                  <span v-if="r.order_status === 'damage'">
                    <span class="label" style="background-color:#FFCDD2; color:#B71C1C;">damage</span>
                  </span>
                  <span v-if="r.order_status === 'return'">
                    <span class="label" style="background-color:#FFE0B2; color:#E65100;">return</span>
                  </span>
                  <br />
                  {{ $t('columns.customerId') }}: {{ r.user_id }} , <br />
                  {{ $t('columns.name') }}: {{ r.full_name }} , <br />
                  <span v-if="r.phone_number">{{ $t('columns.phone') }}: {{ r.phone_number }} , <br /></span>
                  <span v-if="r.phone_number_2">{{ $t('columns.phone') }} 2: {{ r.phone_number_2 }} , <br /></span>
                  <span v-if="r.order_date != null">{{ r.order_date }}</span>
                  <span v-else>{{ r.created_at }}</span> <br />
                  {{ $t('columns.confirmDate') }}: {{ r.confirm_date }} , <br />
                  {{ $t('columns.purchaseDate') }}: {{ r.purchase_date | moment('YYYY-MM-DD hh:mm A') }} , <br />
                  <a
                    v-if="$_has_perm('view_order_detail')"
                    style="color:#01579B; display: inline-block" class="rtl-pr-3"
                    :tooltip="$t('orders.viewDetails')"
                    @click="initDetail('modal_showApp', r.order_id)"
                  >
                    <i class="material-icons">aspect_ratio</i>
                  </a>
                  <a
                    v-if="$_has_perm('view_order_shipping_invoice') && r.order_invoice_id_fk"
                    style="color:#00695C; display: inline-block" class="rtl-pr-3"
                    :tooltip="$t('orders.viewShippingInvoiceTooltip')"
                    @click="viewOrderShippingInvoice(r)"
                  >
                    <i class="material-icons">receipt</i>
                  </a>
                  <a
                    v-if="$_has_perm('move_to_damage')"
                    style="color:#D32F2F; display: inline-block" class="rtl-pr-3"
                    :tooltip="$t('orders.moveToDamageTooltip')"
                    @click="moveToDamage(r.order_id)"
                  >
                    <i class="material-icons">broken_image</i>
                  </a>
                  <a
                    v-if="$_has_perm('move_to_return')"
                    style="color:#FF9800; display: inline-block" class="rtl-pr-3"
                    :tooltip="$t('orders.moveToReturnTooltip')"
                    @click="moveToReturn(r.order_id)"
                  >
                    <i class="material-icons">assignment_return</i>
                  </a>
                </td>
              </tr>
              <tr id="no-result-row">
                <td
                  colspan="3"
                  class="text-center"
                >
                  <v-alert
                    type="error"
                    :value="alertValue"
                  >
                    {{ $t('common.noResultsFound') }}
                  </v-alert>
                </td>
              </tr>
            </tbody>
          </mobile-datatable>

          <view-order-details
            :detialData="detialData"
            :closeModal="closeModal"
          ></view-order-details>

          <create-qr-code
            :openGenerateQrCodeModal="openGenerateQrCodeModal"
            :generateQrCodeModal="generateQrCodeModal"
            :closeGenerateQrCodeModal="closeGenerateQrCodeModal"
            :qrCodeValues="qrCodeValues"
            :generateQrCode="generateQrCode"
            :getResults="getResults"
            :advanceToIntShipment="true"
          ></create-qr-code>

          <admin-cancel-order
            :cancelOrderModal="cancelOrderModal"
            :closeCancelingOrderModal="closeCancelingOrderModal"
            :cancelAlert="cancelAlert"
            :cancelData="cancelData"
            :cancelingOrder="cancelingOrder"
            :reasons="reasons"
            :willAutoRefund="cancelWillAutoRefund"
            :refundAmount="cancelRefundAmount"
          ></admin-cancel-order>

          <!-- Image Lightbox -->
          <v-dialog v-model="lightboxOpen" max-width="600" @input="val => { if (!val) lightboxOpen = false }">
            <v-card style="background:#000; text-align:center; padding:8px;">
              <img v-if="lightboxImage" :src="lightboxImage" style="max-width:100%; max-height:80vh; object-fit:contain; border-radius:4px;" />
              <div style="margin-top:8px;"><v-btn flat dark @click="lightboxOpen = false">{{ $t('common.close') }}</v-btn></div>
            </v-card>
          </v-dialog>

          <!-- Change Status Dialog -->
          <v-dialog v-model="changeStatusDialog" max-width="460">
            <v-card>
              <v-card-title>
                <div style="font-size:16px; font-weight:600; width:100%;">{{ $t('changeOrderStatus.title') }}</div>
                <div v-if="changeStatusBulkMode" style="font-size:13px; color:#666; font-weight:400; padding-top:2px;">
                  <strong>{{ qrCodeValues.length }}</strong> {{ $t('changeOrderStatus.ordersSelectedSuffix') }}
                </div>
                <div v-else-if="changeStatusOrder" style="font-size:13px; color:#666; font-weight:400; padding-top:2px;">
                  {{ $t('changeOrderStatus.orderColonPrefix') }} <strong>{{ changeStatusOrder.theOrderID }}</strong>
                </div>
              </v-card-title>
              <v-card-text style="padding-top:12px; overflow:visible;">
                <v-select
                  v-model="selectedNewStatus"
                  :items="statuses.filter(s => s.value)"
                  item-text="text"
                  item-value="value"
                  :label="$t('delivery.selectStatusLabel')"
                  dense
                  hide-details
                ></v-select>

                <!-- Purchase fields -->
                <v-alert
                  v-if="selectedNewStatus === 'purchased' && isPureFallbackSelection"
                  type="info"
                  :value="true"
                  style="margin-top:12px;"
                >
                  {{ $t('changeOrderStatus.alreadyPurchasedBeforeInfo') }}
                </v-alert>
                <template v-if="selectedNewStatus === 'purchased' && !isPureFallbackSelection">
                  <v-select
                    :items="banks"
                    item-text="text"
                    item-value="value"
                    v-model="purchaseFields.bank_id"
                    :label="$t('columns.bank')"
                    dense
                    class="mt-3"
                  ></v-select>
                  <v-text-field
                    v-if="!changeStatusBulkMode"
                    :label="$t('purchaseOrder.actualPriceLabel')"
                    v-model.number="purchaseFields.actual_price"
                    type="number"
                    :step="0.01"
                    dense
                  ></v-text-field>
                  <div v-else style="font-size:12px; color:#888; margin-bottom:8px;">
                    {{ $t('changeOrderStatus.actualPriceCalculatedAutomatically') }}
                  </div>
                  <v-text-field
                    :label="$t('columns.orderNumber')"
                    v-model="purchaseFields.barcode"
                    dense
                  ></v-text-field>
                  <v-text-field
                    :label="$t('columns.warehouseDate')"
                    v-model="purchaseFields.warehouse_date"
                    type="date"
                    dense
                  ></v-text-field>
                  <v-text-field
                    :label="$t('columns.fromDate')"
                    v-model="purchaseFields.from_date"
                    type="date"
                    dense
                  ></v-text-field>
                  <v-text-field
                    :label="$t('columns.toDate')"
                    v-model="purchaseFields.to_date"
                    type="date"
                    dense
                  ></v-text-field>
                  <v-text-field
                    :label="$t('columns.note')"
                    v-model="purchaseFields.purchase_note"
                    dense
                  ></v-text-field>
                </template>

                <!-- Cancel fields -->
                <template v-if="selectedNewStatus === 'canceled'">
                  <v-alert
                    type="warning"
                    :value="!!changeStatusRefundWarning"
                    style="margin-bottom:8px;"
                  >
                    {{ changeStatusRefundWarning }}
                  </v-alert>
                  <v-select
                    :items="reasons"
                    item-text="text"
                    item-value="value"
                    v-model="cancelFields.selectedReason"
                    :label="$t('adminCancelOrder.cancelReasonListLabel')"
                    dense
                    class="mt-3"
                    @input="updateCancelReasonFields"
                  ></v-select>
                  <v-text-field
                    :label="$t('adminCancelOrder.cancelReasonEnglishLabel')"
                    v-model="cancelFields.cancel_reason"
                    dense
                  ></v-text-field>
                  <v-text-field
                    :label="$t('adminCancelOrder.cancelReasonArabicLabel')"
                    v-model="cancelFields.cancel_reason_ar"
                    dense
                  ></v-text-field>
                  <v-text-field
                    :label="$t('adminCancelOrder.cancelReasonKurdishLabel')"
                    v-model="cancelFields.cancel_reason_ku"
                    dense
                  ></v-text-field>
                </template>

                <!-- Refund fields -->
                <template v-if="selectedNewStatus === 'refunded'">
                  <v-select
                    :items="reasons"
                    item-text="text"
                    item-value="value"
                    v-model="refundFields.selectedReason"
                    :label="$t('changeOrderStatus.refundReasonRequiredLabel')"
                    dense
                    class="mt-3"
                    @input="updateRefundReasonFields"
                  ></v-select>
                  <v-text-field
                    :label="$t('columns.note')"
                    v-model="refundFields.refund_note"
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-if="!changeStatusBulkMode"
                    :label="$t('changeOrderStatus.moneyAmountLabel')"
                    v-model.number="refundFields.money_amount"
                    type="number"
                    :step="0.01"
                    dense
                  ></v-text-field>
                  <div v-else style="font-size:12px; color:#888; margin-bottom:8px;">
                    {{ $t('changeOrderStatus.eachOrderRefundedFullRemaining') }}
                  </div>
                </template>
              </v-card-text>
              <v-card-actions style="padding-bottom:12px;">
                <v-spacer></v-spacer>
                <v-btn flat @click="changeStatusDialog = false" style="padding: 0 16px !important;">{{ $t('common.cancel') }}</v-btn>
                <v-btn color="primary" :disabled="!selectedNewStatus || changeStatusSaving || (!changeStatusBulkMode && changeStatusOrder && selectedNewStatus === changeStatusOrder.order_status)" @click="saveOrderStatus" style="padding: 0 16px !important;">
                  {{ changeStatusSaving ? $t('changeOrderStatus.savingLabel') : $t('common.save') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <process-the-order
            v-if="currentProcessOrder"
            ref="processComp"
            :processOrder="processOrder"
            :r="currentProcessOrder"
            :checkAlert="checkAlert"
            :updateData="updateData"
            :calculateTotalAmount="calculateTotalAmount"
            :loading="loading"
            :getResults="getResults"
            :clearProcessOrderData="clearProcessOrderData"
            :theQuantity="theQuantity"
            :itemTypes="itemTypes"
            :warehouses="warehouses"
            :reasons="reasons"
          ></process-the-order>

          <add-order-invoice
            ref="addOrderInvoice"
            :formData="generateInvoiceData"
            :orderInvoiceModal="orderInvoiceModal"
            @close="orderInvoiceModal = false; qrCodeValues = []"
            @invoice-created="onInvoiceCreated()"
            @view-clicked="onInvoiceViewed()"
          ></add-order-invoice>

          <customer-delivery-summary
            :summaryModal="customerDeliverySummaryModal"
            :apiResults="apiResults"
            :fetchUrl="'api/all-order'"
            :queryParams="tableData"
            @close="customerDeliverySummaryModal = false"
            @cleared="getResults()"
          ></customer-delivery-summary>

          <div class="row">
            <div class="col-xs-12 text-center oc-pagination">
              <paginate
                :page-count="pagination.lastPage"
                :page-range="2"
                :changePage="changePage"
                :container-class="'pagination'"
                :pagination="pagination"
                :page-class="'page-item'"
              ></paginate>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationVue from './Pagination.vue'
import mobileDatatable from './mobileDatatable'
import preDatatableComponent from './preDatatableComponent.vue'
import DatatableVue from './Datatable.vue'
import viewOrderDetails from './viewOrderDetails.vue'
import adminCancelOrder from './adminCancelOrder.vue'
import processTheOrder from './processTheOrder.vue'
import createQrCode from './createQrCode.vue'
import AddOrderInvoice from './orderInvoice/AddOrderInvoice.vue'
import customerDeliverySummary from './customerDeliverySummary.vue'
import axios from 'axios'
import swal from 'sweetalert'
import { withSelectAllOption, expandSelectAll } from '../mixins/filterSelectAll'
import statusHelpers from '../mixins/statusHelpers'
import toolbarButtonDisplay from '../mixins/toolbarButtonDisplay'

export default {
  mixins: [statusHelpers, toolbarButtonDisplay],
  components: {
    preDatatableComponent: preDatatableComponent,
    datatable: DatatableVue,
    paginate: PaginationVue,
    'view-order-details': viewOrderDetails,
    'mobile-datatable': mobileDatatable,
    'admin-cancel-order': adminCancelOrder,
    'process-the-order': processTheOrder,
    'create-qr-code': createQrCode,
    'add-order-invoice': AddOrderInvoice,
    'customer-delivery-summary': customerDeliverySummary
  },
  created() {
    this.getResults()

    try {
      const cachedSettings = JSON.parse(localStorage.getItem('app_settings'))
      // Default (missing/unparsed setting) is "locked" — same as the backend's default of false,
      // so a canceled order is only editable once the setting is explicitly turned on.
      this.lockCanceledOrderStatusChange = !(cachedSettings && cachedSettings.allow_canceled_order_status_change === true)
    } catch (e) {
      this.lockCanceledOrderStatusChange = true
    }
  },
  mounted() {
    this.perPageFix()

    if (screen.width <= 600) {
      this.isMobile = true
    } else {
      this.isMobile = false
    }

    this.getWarehouses()
    this.getStatuses()
    this.getItemType()
    this.getReasons()
    this.getBanks()
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    let sortOrders = {}
    let columns = [
      { label: '', name: '', text: this.$t('columns.checkBoxColumn'), value: '1' },
      {
        label: this.$t('columns.orderInfo'),
        text: this.$t('columns.orderInfo'),
        value: '2',
        sortable: true,
        searchable: true,
        name: 'theOrderID'
      },
      {
        label: this.$t('columns.customer'),
        text: this.$t('columns.customer'),
        value: '3',
        sortable: true,
        searchable: true,
        name: 'full_name'
      },
      {
        label: this.$t('columns.itemDetails'),
        text: this.$t('columns.itemDetails'),
        value: '4',
        sortable: true,
        searchable: true,
        name: 'product_name'
      },
      {
        label: this.$t('columns.product'),
        text: this.$t('columns.product'),
        value: '5',
        sortable: false,
        searchable: false,
        name: 'image'
      },
      {
        label: this.$t('columns.statusLocation'),
        text: this.$t('columns.statusLocation'),
        value: '6',
        sortable: true,
        searchable: true,
        name: 'order_status'
      },
      {
        label: this.$t('columns.pricing'),
        text: this.$t('columns.pricing'),
        value: '7',
        sortable: false,
        searchable: false,
        name: 'price_all'
      },
      {
        label: this.$t('columns.datePayment'),
        text: this.$t('columns.datePayment'),
        value: '8',
        sortable: true,
        searchable: true,
        name: 'the_date'
      },
      {
        label: this.$t('columns.actions'),
        text: this.$t('columns.actions'),
        value: '9',
        sortable: false,
        searchable: false,
        name: 'actions'
      },
    ]
    columns.forEach(column => {
      sortOrders[column.name] = -1
    })

    return {
      lightboxImage: null,
      lightboxOpen: false,
      qrCodeValues: [],
      isMobile: false,
      // Hidden by default (per-page preference, same localStorage convention as
      // sortDir_allOrder/cols_allOrder_v3) -- only shown once a staff member opts in.
      showFilters: localStorage.getItem('filtersVisible_allOrder') === 'true',
      loader: null,
      loading: false,
      url: 'api/all-order',
      orderDetailUrl: 'api/show_order_detail',
      processOrderUrl: 'api/orders',
      cancelingOrderUrl: 'api/canceling_order_by_admin',
      moveToDamageUrl: 'api/move_to_damage',
      moveToReturnUrl: 'api/move_to_return',
      getItemTypeUrl: 'api/get-item-type',
      rowIndex: '',
      alertValue: false,
      checkAlert: false,
      lockCanceledOrderStatusChange: false,
      // Change Status Dialog
      changeStatusDialog: false,
      changeStatusOrder: null,
      changeStatusBulkMode: false,
      selectedNewStatus: null,
      changeStatusSaving: false,
      banks: [],
      kurdistanBankId: null,
      purchaseFields: {
        bank_id: null,
        actual_price: null,
        barcode: null,
        warehouse_date: null,
        from_date: null,
        to_date: null,
        purchase_note: ''
      },
      cancelFields: {
        selectedReason: null,
        cancel_reason: '',
        cancel_reason_ar: '',
        cancel_reason_ku: ''
      },
      refundFields: {
        selectedReason: null,
        refund_reason: '',
        refund_note: 'full refund',
        money_amount: null
      },
      // QR Code
      generateQrCodeModal: false,
      // Customer Delivery Summary
      customerDeliverySummaryModal: false,
      // Cancel Order
      cancelOrderModal: false,
      cancelAlert: false,
      cancelData: {
        cancel_reason: '',
        cancel_reason_ar: '',
        cancel_reason_ku: ''
      },
      theCancelOrderID: '',
      cancelWillAutoRefund: false,
      cancelRefundAmount: null,
      refundOnCancelStatuses: ['purchased', 'warehouse_delivered_order', 'pending_to_shipment', 'int_shipment', 'pending_to_branch', 'branch_delivery', 'delivered'],
      reasons: [],
      // Process Order
      temp_shipping: '',
      itemTypeResult: [],
      itemTypes: [],
      warehouses: [],
      statuses: [],
      warehouseFilterVal: [],
      statusFilterVal: [],
      paymentFilterVal: [],
      paymentFilterItems: [
        { text: this.$t('invoice.unpaidStatus'), value: 'unpaid' },
        { text: this.$t('invoice.partialPaidStatus'), value: 'partial_paid' },
        { text: this.$t('invoice.paidStatus'), value: 'paid' },
        { text: this.$t('orders.cancelTooltip'), value: 'cancel' },
      ],
      officeStatusFilterVal: [],
      officeStatusFilterItems: [
        { text: this.$t('invoice.officeOption'), value: 'office' },
        { text: this.$t('columns.outOffice'), value: 'out_office' }
      ],
      platformFilterItems: [
        { text: this.$t('common.mobileSource'), value: 'mobile' },
        { text: this.$t('common.webSource'), value: 'web' },
        { text: this.$t('common.adminSource'), value: 'admin' }
      ],
      orderSortDirItems: [
        { text: this.$t('orders.newestFirst'), value: 'desc' },
        { text: this.$t('orders.oldestFirst'), value: 'asc' }
      ],
      clearedFilterVal: 'all',
      clearedFilterItems: [
        { text: this.$t('common.active'), value: 'active' },
        { text: this.$t('columns.cleared'), value: 'cleared' },
        { text: this.$t('common.all'), value: 'all' }
      ],
      dateTypeFilter: 'orders.created_at',
      dateTypeItems: [
        { text: this.$t('orders.sortByOrder'), value: 'orders.created_at' },
        { text: this.$t('orders.sortByConfirm'), value: 'orders.confirm_date' },
        { text: this.$t('orders.sortByPurchase'), value: 'orders.purchase_date' },
      ],
      theQuantity: { quantity: '' },
      updateData: {
        item_price: null,
        the_tl_price: null,
        the_aed_price: null,
        the_cny_price: null,
        the_gbp_price: null,
        the_eur_price: null,
        tax: null,
        national_tax: null,
        item_weight: null,
        internal_shipping_handling: null,
        delivery_price: null,
        kreenex_fee: null,
        quantity_charge: null,
        insurance: null,
        total_amount: null,
        from_date: null,
        to_date: null,
        description: '',
        tmp_shipping: null,
        item_weight_lb: null,
        price_weight: null,
        send_sms: false,
        discount: null,
        item_type: '',
        transportation_type: null,
        is_repackage: false,
        is_hold: false,
        other_service: '',
        local_delivery: 0,
        process_note: '',
        customer_process_note: '',
        repackage_price: null,
        hold_price: null,
        other_service_price: null,
        customer_id_fk: null,
        product_name: null,
        product_url: null,
        quantity: null,
        color: null,
        size: null,
        note: null,
        brand: null
      },
      // Invoice
      orderInvoiceModal: false,
      generateInvoiceData: {},
      // Process modal (single instance outside v-for)
      currentProcessOrder: null,
      btnsave: true,
      filterMade: false,
      totalResult: 0,
      resultsVersion: 0,
      queryParameter: true,
      orderID: [],
      detialData: {
        the_tl_price: '',
        order_id: '',
        cancel_reason: '',
        full_name: '',
        bank_name: '',
        email: '',
        phone_number: '',
        c_o_d: '',
        product_url: '',
        product_name: '',
        description: '',
        size: '',
        theOrderID: '',
        type: '',
        coupon_percentage: '',
        status: '',
        color: '',
        quantity: '',
        quantity_charge: '',
        coupon: '',
        qrcode: '',
        barcode: '',
        delivery_type: '',
        item_weight: '',
        item_price: '',
        discount: '',
        tax: '',
        internal_shipping_handling: '',
        kreenex_fee: '',
        delivery_price: '',
        insurance: '',
        total_amount: '',
        from_date: '',
        to_date: '',
        confirm_date: '',
        refund_reason: '',
        country: '',
        order_date: '',
        track_number: '',
        item_type: '',
        note: ''
      },
      prevSearchQuery: '',
      apiResults: [],
      detailResults: [],
      columns: columns,
      defaultHideCol: [],
      sortKey: 'order_id',
      sortOrders: sortOrders,
      dependDate: 'orders.created_at',
      tableData: {
        per_page: 10,
        search: '',
        column: 'order_id',
        sortDir: localStorage.getItem('sortDir_allOrder') || 'desc',
        searchOneCol: '',
        searchOneVal: '',
        from_date: '',
        to_date: '',
        depend_date: '',
        branches_id: '',
        warehouse_id_fk: '',
        status_filter: '',
        payment_filter: '',
        office_status_filter: '',
        platform: '',
        cleared_filter: 'all'
      },
      pagination: {
        lastPage: 1,
        currentPage: 1,
        total: 1,
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: ''
      }
    }
  },

  computed: {
    warehousesWithSelectAll() {
      return withSelectAllOption(this.warehouses)
    },

    statusesWithSelectAll() {
      return withSelectAllOption(this.statuses)
    },

    paymentFilterItemsWithSelectAll() {
      return withSelectAllOption(this.paymentFilterItems)
    },

    summaryTotalOrders() {
      if (this.qrCodeValues.length > 0) return this.qrCodeValues.length
      return this.filterMade
        ? this.apiResults.length
        : (this.pagination.total || this.apiResults.length)
    },
    summaryTotalAmount() {
      if (this.qrCodeValues.length > 0) {
        return this.qrCodeValues.reduce((sum, v) => sum + (Number(v[10]) || 0), 0)
      }
      return this.apiResults.reduce((sum, r) => sum + (Number(r.total_amount) || 0), 0)
    },
    // Best-effort only: counts within the currently loaded page of apiResults, not a true
    // system-wide count (that would need a dedicated backend aggregate, out of scope for this
    // visual redesign) — hence the "(current page)" note shown alongside it in the stat card.
    ordersTodayCount() {
      const today = new Date().toISOString().split('T')[0]
      return this.apiResults.filter(r => {
        const d = r.order_date || r.created_at
        return d && d.substr(0, 10) === today
      }).length
    },
    // Same current-page-only caveat as ordersTodayCount above.
    pendingPaymentsCount() {
      return this.apiResults.filter(r => r.payment_status && r.payment_status !== 'paid').length
    },
    changeStatusRefundWarning() {
      if (this.selectedNewStatus !== 'canceled') return null

      if (!this.changeStatusBulkMode) {
        if (this.changeStatusOrder && this.orderNeedsAutoRefundOnCancel(this.changeStatusOrder)) {
          return this.$t('changeOrderStatus.orderAlreadyPurchasedRefundWarning', { amount: this.changeStatusOrder.total_amount })
        }
        return null
      }

      const selectedIds = this.qrCodeValues.map(v => v[0])
      const affectedCount = this.apiResults.filter(r =>
        selectedIds.includes(r.order_id) && this.orderNeedsAutoRefundOnCancel(r)
      ).length

      return affectedCount > 0
        ? `${affectedCount} of the selected order(s) have already been purchased/shipped — canceling them will also automatically issue each a full refund of its remaining balance.`
        : null
    },
    isPureFallbackSelection() {
      if (!this.selectedNewStatus) return false

      if (this.changeStatusBulkMode) {
        const selectedIds = this.qrCodeValues.map(v => v[0])
        const selectedRows = this.apiResults.filter(r => selectedIds.includes(r.order_id))
        return selectedRows.length > 0 && selectedRows.every(r =>
          r.order_status !== this.selectedNewStatus && this.hasAlreadyCompletedStatus(r, this.selectedNewStatus)
        )
      }

      return !!this.changeStatusOrder &&
        this.changeStatusOrder.order_status !== this.selectedNewStatus &&
        this.hasAlreadyCompletedStatus(this.changeStatusOrder, this.selectedNewStatus)
    },
  },

  watch: {
    warehouseFilterVal(newVal) {
      const expanded = expandSelectAll(newVal, this.warehouses.map(i => i.value))
      if (expanded !== newVal) {
        this.warehouseFilterVal = expanded
        return
      }
      this.handleWarehouseFilter(newVal)
    },

    statusFilterVal(newVal) {
      const expanded = expandSelectAll(newVal, this.statuses.map(i => i.value))
      if (expanded !== newVal) {
        this.statusFilterVal = expanded
        return
      }
      this.handleStatusFilter(newVal)
    },

    paymentFilterVal(newVal) {
      const expanded = expandSelectAll(newVal, this.paymentFilterItems.map(i => i.value))
      if (expanded !== newVal) {
        this.paymentFilterVal = expanded
        return
      }
      this.handlePaymentFilter(newVal)
    },

    officeStatusFilterVal(newVal) {
      this.handleOfficeStatusFilter(newVal)
    },

    clearedFilterVal(newVal) {
      this.handleClearedFilter(newVal)
    }
  },

  methods: {
    // Mirrors OrderController::hasAlreadyCompletedStatus() — checked via the persistent marker
    // column each one-time transition sets and never clears, NOT the order's current status
    // position. An order can be moved backward past where it started (e.g. branch_delivery ->
    // new_order) and then forward again; a rank/position comparison would wrongly treat that as
    // a first-time transition and re-require already-collected purchase data.
    hasAlreadyCompletedStatus(row, targetStatus) {
      if (targetStatus === 'processed_order') return !!row.process_date
      if (targetStatus === 'approved_order') return !!row.confirm_date
      if (targetStatus === 'purchased') return !!row.purchase_date
      return false
    },

    checkAll() {
      for (let i = 0; i < this.apiResults.length; i++) {
        if (!$('#cb' + i).is(':checked')) {
          $('#cb' + i).click()
        }
      }
    },

    unCheckAll() {
      for (let i = 0; i < this.apiResults.length; i++) {
        if ($('#cb' + i).is(':checked')) {
          $('#cb' + i).click()
        }
      }
    },

    branchEdit(branches) {
      this.tableData.branches_id = branches.toString()
      this.getResults()
    },

    toggleFiltersVisible() {
      this.showFilters = !this.showFilters
      localStorage.setItem('filtersVisible_allOrder', this.showFilters ? 'true' : 'false')
    },

    // Replicates preDatatableComponent.vue's own built-in Reload button (now relocated
    // into the panel-heading here) -- reaches into the child's own filter-chip/date-range
    // reset state via $refs since that state lives inside the shared component, not here.
    reloadTable() {
      this.resetGetResults()
      const preDatatable = this.$refs.preDatatable
      if (preDatatable) {
        preDatatable.filterApplied = false
        preDatatable.resetDatePickerDefaults()
        preDatatable.ware = 'Select'
      }
    },

    // Scoped to #datatable (this page's own <datatable> table) and deterministic set/unset —
    // the old unscoped $('td:nth-child(N)') selector matched EVERY table on the page, including
    // the Generate/View Invoice modal's items-table (also 7 columns), silently hiding its Total
    // cell whenever this page's own column visibility got applied. See orderInvoices.vue's
    // showHideColumn for the same fix.
    showHideColumn(prevColVal, currentColVal) {
      const hidden = {}
      currentColVal.forEach((v) => { hidden[v] = true })

      for (let i = 1; i <= this.columns.length; i++) {
        const selector = '#datatable td:nth-child(' + i + '),#datatable th:nth-child(' + i + ')'
        if (hidden[i]) {
          $(selector).hide()
        } else {
          $(selector).show()
        }
      }
    },

    getDependDate(date) {
      this.dependDate = date
    },

    handleDateTypeFilter(val) {
      this.dateTypeFilter = val || 'orders.created_at'
      this.dependDate = this.dateTypeFilter
    },

    resetGetResults2() {
      this.queryParameter = false
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      this.tableData.search = ''
      this.tableData.from_date = ''
      this.tableData.to_date = ''
      this.tableData.depend_date = 'orders.created_at'
      this.tableData.warehouse_id_fk = ''
      this.tableData.status_filter = ''
      this.tableData.payment_filter = ''
      this.tableData.office_status_filter = ''
      this.tableData.platform = ''
      this.tableData.cleared_filter = 'all'
      this.warehouseFilterVal = []
      this.statusFilterVal = []
      this.paymentFilterVal = []
      this.officeStatusFilterVal = []
      this.clearedFilterVal = 'all'
      this.dependDate = 'orders.created_at'
      this.dateTypeFilter = 'orders.created_at'
      this.getResults()
    },

    getResults(url = this.url) {
      var token = localStorage.getItem('access_token')
      let theDatatable = document.querySelector('.the-datatable')

      // A selection made before a filter/search/reload must not silently carry over into
      // the new result set — otherwise a stale row (no longer even visible) stays selected
      // and gets included in the next bulk action, affecting rows the user never intended
      // to touch in the current (filtered) view.
      this.qrCodeValues = []

      if (theDatatable != null) {
        theDatatable.classList.add('slide-table-out')
      }

      axios
        .get(url, {
          params: this.tableData,
          headers: { Authorization: 'Bearer ' + token }
        })
        .then(response => {
          let data = response.data
          const isFlatArray = Array.isArray(data.data)
          if (isFlatArray) {
            this.filterMade = true
            this.apiResults = data.data
          } else {
            this.filterMade = false
            this.apiResults = data.data.data
          }
          this.resultsVersion++

          if (this.apiResults.length === 0) {
            this.tableData.searchOneCol = ''
            this.tableData.searchOneVal = ''
            this.tableData.search = ''
            this.alertValue = true
          } else {
            this.alertValue = false
          }
          if (isFlatArray) {
            this.removeConfigPagination()
          } else {
            this.configPagination(data.data)
          }
          this.getOrderID(this.apiResults)
          if (theDatatable) {
            theDatatable.classList.remove('slide-table-out')
          }
        })
        .then(() => {
          this.totalResult = 0
          for (let i = 0; i < this.apiResults.length; i++) {
            this.totalResult += 1
          }
        })
        .catch(errors => { console.error(errors) })
    },

    removeConfigPagination() {
      this.pagination.lastPage = ''
      this.pagination.currentPage = ''
      this.pagination.total = ''
      this.pagination.lastPageUrl = ''
      this.pagination.nextPageUrl = ''
      this.pagination.prevPageUrl = ''
      this.pagination.from = ''
      this.pagination.to = ''
    },

    getOrderID(orders) {
      this.orderID = []
      for (let i = 0; i < orders.length; i++) {
        this.orderID[i] = orders[i].order_id
      }
    },

    resetGetResults() {
      this.queryParameter = false
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      this.tableData.search = ''
      this.tableData.from_date = ''
      this.tableData.to_date = ''
      this.tableData.warehouse_id_fk = ''
      this.tableData.status_filter = ''
      this.tableData.payment_filter = ''
      this.tableData.office_status_filter = ''
      this.tableData.platform = ''
      this.tableData.cleared_filter = 'all'
      this.warehouseFilterVal = []
      this.statusFilterVal = []
      this.paymentFilterVal = []
      this.officeStatusFilterVal = []
      this.clearedFilterVal = 'all'
      // filterTable() already re-fetches (getResults()) with the reset filters above — sort
      // (column/sortKey/sortDir) is deliberately left untouched here. sortByColumn() used to be
      // called with no argument to "just refresh", but it's the column-header-click handler: it
      // unconditionally flips sortDir and blanks column/sortKey to undefined, which is why Reload
      // Table used to toggle newest-first/oldest-first on every click instead of just refreshing.
      this.filterTable(this.tableData.from_date, this.tableData.to_date)
    },

    perPageFix(perPage) {
      this.tableData.per_page = perPage
      this.getResults()
    },

    sendToExport() {
      let searchValue = this.tableData.search !== '' ? this.tableData.search : '0'
      let column = this.tableData.column !== '' ? this.tableData.column : '0'
      let sortDir = this.tableData.sortDir !== '' ? this.tableData.sortDir : '0'
      let searchedOneCol = this.tableData.searchOneCol !== '' ? this.tableData.searchOneCol : '0'
      let searchedOneVal = this.tableData.searchOneVal !== '' ? this.tableData.searchOneVal : '0'
      let fromDate = this.tableData.from_date !== '' ? this.tableData.from_date : '0'
      let toDate = this.tableData.to_date !== '' ? this.tableData.to_date : '0'
      let loggedInUserData = JSON.parse(localStorage.getItem('user_data'))
      let warehouses = loggedInUserData.warehouse_id_fk
      let perPage = this.tableData.per_page == null ? 10 : this.tableData.per_page
      if (
        searchValue !== '0' ||
        searchedOneCol !== '0' ||
        searchedOneVal !== '0' ||
        toDate !== '0' ||
        fromDate !== '0'
      ) {
        perPage = '0'
      }

      let hostName = window.location.hostname
      let protocol = window.location.protocol + '//'
      let fullHostName = protocol + hostName

      window.location.replace(
        fullHostName +
        '/api/order-export?searchValue=' +
        searchValue +
        '&column=' +
        column +
        '&sortDir=' +
        sortDir +
        '&depend_date=' +
        this.dependDate +
        '&searchedOneCol=' +
        searchedOneCol +
        '&searchedOneVal=' +
        searchedOneVal +
        '&from_date=' +
        fromDate +
        '&to_date=' +
        toDate +
        '&warehouses=' +
        warehouses +
        '&per_page=' +
        perPage +
        '&all_order=true'
      )
    },

    modal(modal) {
      $('#' + modal).modal({ backdrop: 'static' })
    },

    closeModal(modal) {
      let theModal = $('#' + modal)
      theModal.modal('toggle')
    },

    initDetail(modal, id) {
      let token = localStorage.getItem('access_token')
      axios
        .get(this.orderDetailUrl + '/' + id, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then(response => {
          let data = response.data
          this.detailResults = data.data

          this.detialData = this.detailResults[0]
        })
      this.modal(modal)
    },

    // Opens the order's linked shipping invoice using AddOrderInvoice.vue's own already-working
    // openShippingInvoiceView() (same component/view "Save and view" and orderInvoices.vue's own
    // "View" action use) rather than duplicating that fetch-and-display logic here. Passes {} so
    // the print log records this as a plain view (matching orderInvoices.vue's own "View"), not
    // openShippingInvoiceView()'s default is_save_and_view flag.
    viewOrderShippingInvoice(r) {
      if (!r.order_invoice_id_fk) return
      this.$refs.addOrderInvoice.openShippingInvoiceView(r.order_invoice_id_fk, {})
    },

    changePage(pageUrl) {
      this.url = pageUrl
      this.getResults()
    },

    searchMe(str) {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      if (str.trim() !== '') {
        this.tableData.search = str
        this.prevSearchQuery = str
        this.getResults()
      } else {
        swal({
          text: this.$t('common.typeToSearch'),
          icon: 'warning',
          dangerMode: true
        })
        if (this.prevSearchQuery !== '') {
          this.tableData.search = ''
          this.prevSearchQuery = ''
          this.getResults()
        }
      }
    },

    sortByColumn(str) {
      this.tableData.column = str
      this.tableData.sortKey = str
      this.tableData.sortDir = this.tableData.sortDir === 'desc' ? 'asc' : 'desc'
      this.getResults()
    },

    searchOneCol(columnName, str) {
      this.tableData.searchOneCol = columnName
      this.tableData.searchOneVal = str
      this.getResults()
    },

    filterTable(fromDate, toDate) {
      this.tableData.from_date = fromDate
      this.tableData.to_date = toDate
      this.tableData.depend_date = this.dependDate
      this.getResults()
    },

    configPagination(data) {
      this.pagination.lastPage = data.last_page
      this.pagination.currentPage = data.current_page
      this.pagination.total = data.total
      this.pagination.lastPageUrl = data.last_page_url
      this.pagination.nextPageUrl = data.next_page_url
      this.pagination.prevPageUrl = data.prev_page_url
      this.pagination.from = data.from
      this.pagination.to = data.to
    },

    handleWarehouseFilter(val) {
      this.tableData.warehouse_id_fk = Array.isArray(val) && val.length ? val.join(',') : ''
      this.url = 'api/all-order'
      this.getResults()
    },

    handleStatusFilter(val) {
      this.tableData.status_filter = Array.isArray(val) && val.length ? val.join(',') : ''
      this.url = 'api/all-order'
      this.getResults()
    },

    handlePaymentFilter(val) {
      this.tableData.payment_filter = Array.isArray(val) && val.length ? val.join(',') : ''
      this.url = 'api/all-order'
      this.getResults()
    },

    handleOfficeStatusFilter(val) {
      this.tableData.office_status_filter = Array.isArray(val) && val.length ? val.join(',') : ''
      this.url = 'api/all-order'
      this.getResults()
    },

    handlePlatformFilter(val) {
      this.tableData.platform = Array.isArray(val) && val.length ? val.join(',') : ''
      this.url = 'api/all-order'
      this.getResults()
    },

    handleClearedFilter(val) {
      this.tableData.cleared_filter = val || 'all'
      this.url = 'api/all-order'
      this.getResults()
    },

    handleOrderSortDirFilter(val) {
      this.tableData.sortDir = val || 'desc'
      this.tableData.column = 'order_id'
      localStorage.setItem('sortDir_allOrder', this.tableData.sortDir)
      this.url = 'api/all-order'
      this.getResults()
    },

    // Process Order modal (single instance pattern)
    openProcessOrderModal(r) {
      // allOrder API returns a flat join — r.warehouse is not included.
      // processTheOrder.vue accesses r.warehouse.transportation_types on mount and in
      // the dialog watcher, so we must enrich r before passing it to the component.
      const warehouseObj = this.warehouses.find(w => w.value === r.warehouse_id_fk)
      const enriched = Object.assign({}, r, {
        warehouse: {
          transportation_types: warehouseObj ? warehouseObj.transportation_types : []
        }
      })
      this.currentProcessOrder = enriched
      this.checkAlert = false
      this.clearProcessOrderData()
      this.theQuantity.quantity = r.quantity
      this.updateData.quantity_charge = r.quantity_charge
      this.$nextTick(() => {
        if (this.$refs.processComp) {
          this.$refs.processComp.dialog = true
          // Single-instance pattern bypasses processTheOrder.vue's own gear-icon click
          // handler, which is what normally fetches these — without them exchangeBase (etc.)
          // stays 0, so editing a non-USD warehouse order's item price silently fails to
          // recalculate the total (reproduced on order TR-748, a Turkey/TL order).
          this.$refs.processComp.calculateTotalAmount()
          this.$refs.processComp.getExchangeRate()
          this.$refs.processComp.getAEDRate()
          this.$refs.processComp.getCNYRate()
          this.$refs.processComp.getGBPRate()
          this.$refs.processComp.getEURRate()
        }
      })
    },

    // QR Code methods
    openQrCodeForRow(r) {
      // Mirrors the checkbox :value array's shape (see the v-for row below) so
      // createQrCode.vue's qr[10] (label price) resolves to total_amount instead of
      // undefined — a truncated array here was why the printed price was always $0.00.
      this.qrCodeValues = [[
        r.order_id,
        r.full_name,
        r.phone_number,
        r.user_id,
        r.theOrderID,
        r.is_ship_only,
        r.product_name,
        r.warehouse_id_fk,
        r.transportation_type,
        r.is_ship_only,
        r.total_amount
      ]]
      this.generateQrCodeModal = true
    },

    openGenerateQrCodeModal() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseAtLeastSelectOneOrder'),
          icon: 'warning',
          dangerMode: true
        })
      } else {
        this.generateQrCodeModal = true
      }
    },

    openCustomerDeliverySummaryModal() {
      this.customerDeliverySummaryModal = true
    },

    confirmUnclearAll() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseAtLeastSelectOneOrder'),
          icon: 'warning',
          dangerMode: true
        })

        return
      }

      const orderIds = this.qrCodeValues.map(v => v[0])

      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('changeOrderStatus.unmarkOrdersAsClearedConfirm', { count: orderIds.length }),
        icon: 'warning',
        buttons: true
      }).then(confirm => {
        if (!confirm) return

        const token = localStorage.getItem('access_token')

        axios
          .post('api/orders/unmark-cleared', { orders: orderIds }, {
            headers: { Authorization: 'Bearer ' + token }
          })
          .then(() => {
            this.qrCodeValues = []
            this.getResults()
          })
          .catch(error => {
            const msg = (error.response && error.response.data && error.response.data.message) || this.$t('orders.failedToUnclearOrders')
            swal(this.$t('common.error'), msg, 'error')
          })
      })
    },

    closeGenerateQrCodeModal() {
      this.generateQrCodeModal = false
      this.qrCodeValues = []
      this.getResults()
    },

    generateQrCode() {
      this.closeGenerateQrCodeModal()
    },

    // Cancel Order methods
    // Damage/return orders are a special case: moveToDamage/moveToReturn can be triggered from
    // ANY prior status, so — unlike refundOnCancelStatuses — the order's current damage/return
    // status alone doesn't say whether money was ever taken. confirm_date is the one-time "this
    // order was charged" marker set only when it's approved, so it's checked directly instead.
    // Mirrors OrderController::needsAutoRefundOnCancel() on the backend, which is authoritative.
    orderNeedsAutoRefundOnCancel(order) {
      if (this.refundOnCancelStatuses.includes(order.order_status)) return true
      return (order.order_status === 'damage' || order.order_status === 'return') &&
        !!order.confirm_date && order.is_refunded !== '1'
    },

    openCancelingOrderModal(order) {
      this.cancelOrderModal = true
      this.theCancelOrderID = order.order_id
      this.cancelWillAutoRefund = this.orderNeedsAutoRefundOnCancel(order)
      this.cancelRefundAmount = order.total_amount || null
    },

    closeCancelingOrderModal() {
      this.cancelOrderModal = false
      this.cancelAlert = false
      this.cancelWillAutoRefund = false
      this.cancelRefundAmount = null
      this.resetCancelData()
    },

    resetCancelData() {
      this.cancelData.cancel_reason = ''
      this.cancelData.cancel_reason_ar = ''
      this.cancelData.cancel_reason_ku = ''
    },

    cancelingOrder() {
      let token = localStorage.getItem('access_token')
      axios
        .put(this.cancelingOrderUrl + '/' + this.theCancelOrderID, this.cancelData, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then(() => {
          this.getResults()
          this.cancelAlert = true
          let self = this
          setTimeout(() => {
            self.closeCancelingOrderModal()
          }, 1500)
        })
    },

    getReasons() {
      let token = localStorage.getItem('access_token')
      axios
        .get('api/reason/list', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let result = response.data
          this.reasons = result.map(r => ({
            text: r.cancel_reason,
            value: r.cancel_reason,
            ar: r.cancel_reason_ar,
            ku: r.cancel_reason_ku
          }))
        })
    },

    // Process Order methods
    getWarehouses() {
      let token = localStorage.getItem('access_token')
      axios
        .get('api/warehouses', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.warehouses = []
          let result = response.data.data
          result.forEach((r) => {
            this.warehouses.push({ text: r.country, value: r.warehouse_id, transportation_types: r.transportation_types, currency: r.currency, estimate_days: r.estimate_days })
          })
        })
    },

    getStatuses() {
      // Populates both this.statuses (the Change-Status dropdown) and
      // this.statusList (via the statusHelpers mixin, used by statusColor/statusLabel
      // below) — statuses hidden by the admin (Order Status Settings page) are
      // excluded from the dropdown entirely, e.g. "Refunded" once hidden.
      this.$_fetch_statuses().then(() => {
        this.statuses = this.$_visible_status_list().map((s) => ({
          text: this.$_status_label(s.type),
          value: s.type
        }))
      })
    },

    getBanks() {
      let token = localStorage.getItem('access_token')
      axios
        .get('api/get_banks', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let result = response.data.data
          result.forEach((c) => {
            this.banks.push({ text: c.bank_name, value: c.bank_id })
            if (c.bank_code === 'kur') {
              this.kurdistanBankId = c.bank_id
            }
          })
        })
    },

    getItemType() {
      let token = localStorage.getItem('access_token')
      axios
        .get(this.getItemTypeUrl, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.itemTypeResult = response.data.data
          this.itemTypeResult.forEach((c) => {
            this.itemTypes.push({
              text: c.type_name,
              value: c.type_name,
              shippingPrice: c.type_int_shipping_price
            })
          })
        })
    },

    calculateTotalAmount() {
      let itemPrice = Number(this.updateData.item_price) || 0
      let shipping = Number(this.updateData.internal_shipping_handling) || 0
      let tax = Number(this.updateData.tax) || 0
      let nationalTax = Number(this.updateData.national_tax) || 0
      let delivery = Number(this.updateData.delivery_price) || 0
      let fee = Number(this.updateData.kreenex_fee) || 0
      let insurance = Number(this.updateData.insurance) || 0
      let qttCharge = Number(this.updateData.quantity_charge) || 0
      let discount = Number(this.updateData.discount) || 0
      let qtt = Number(this.theQuantity.quantity) || 0
      let repackagePrice = Number(this.updateData.repackage_price) || 0
      let holdPrice = Number(this.updateData.hold_price) || 0
      let otherServicePrice = Number(this.updateData.other_service_price) || 0
      const _userData = JSON.parse(localStorage.getItem('user_data') || '{}')
      const _exRate = Number(_userData.ex_rate) || 1
      let localDelivery = (Number(this.updateData.local_delivery) || 0) / _exRate

      // Per-unit shipping, matching newOrder.vue/editOrderCharge.vue: item price and shipping
      // are both charged per unit. Confirmed against live data — 76 of 79 existing orders with
      // qty>1 and shipping>0 match (item_price + shipping) * qty, not this flat variant.
      let total = (itemPrice + shipping) * qtt + tax + nationalTax + delivery + fee + insurance + qttCharge + localDelivery

      if (this.updateData.is_repackage) total += repackagePrice
      if (this.updateData.is_hold) total += holdPrice
      if (otherServicePrice > 0) total += otherServicePrice
      if (discount > 0) total -= discount

      this.updateData.total_amount = total.toFixed(2)
    },

    clearProcessOrderData() {
      this.updateData.item_price = null
      this.updateData.the_tl_price = null
      this.updateData.the_aed_price = null
      this.updateData.the_cny_price = null
      this.updateData.the_gbp_price = null
      this.updateData.the_eur_price = null
      this.updateData.tax = null
      this.updateData.item_weight = null
      this.updateData.internal_shipping_handling = 0
      this.updateData.delivery_price = null
      this.updateData.kreenex_fee = null
      this.updateData.insurance = null
      this.updateData.total_amount = null
      this.updateData.from_date = null
      this.updateData.to_date = null
      this.updateData.quantity_charge = null
      this.updateData.description = ''
      this.updateData.item_type = ''
      this.updateData.item_weight_lb = null
      this.updateData.discount = null
      this.updateData.send_sms = false
      this.updateData.is_repackage = false
      this.updateData.is_hold = false
      this.updateData.other_service = ''
      this.updateData.repackage_price = null
      this.updateData.hold_price = null
      this.updateData.other_service_price = null
      this.updateData.local_delivery = null
      this.updateData.tax = null
      this.updateData.national_tax = null
      this.updateData.process_note = ''
      this.updateData.customer_process_note = ''
      this.updateData.customer_id_fk = null
      this.updateData.product_name = null
      this.updateData.product_url = null
      this.updateData.quantity = null
      this.updateData.color = null
      this.updateData.size = null
      this.updateData.note = null
      this.updateData.brand = null
      this.updateData.order_image = null
      this.checkAlert = false
    },

    processOrder(event, id, stime, stime1, stime2) {
      let token = localStorage.getItem('access_token')
      this.loading = true
      this.updateData.send_sms = this.updateData.send_sms.toString()

      const formData = new FormData()

      Object.keys(this.updateData).forEach(key => {
        if (key === 'customer_process_note_image' || key === 'order_image') return
        if (this.updateData[key] !== null && this.updateData[key] !== undefined) {
          formData.append(key, this.updateData[key])
        }
      })

      if (this.updateData.customer_process_note_image instanceof File) {
        formData.append('customer_process_note_image', this.updateData.customer_process_note_image)
      }

      if (this.updateData.order_image instanceof File) {
        formData.append('order_image', this.updateData.order_image)
      }

      axios.post(this.processOrderUrl + '/' + id, formData, {
        headers: { Authorization: 'Bearer ' + token }
      })
        .then(() => {
          this.loading = false
          this.clearProcessOrderData()
          stime.time = ''
          stime1.time = ''
          stime2.time = ''
          this.checkAlert = true
          this.getResults()
          let b = this
          setTimeout(function () {
            b.checkAlert = false
          }, 1500)
        })
        .catch((error) => {
          const rawMsg = error && error.response && error.response.data && error.response.data.message
          const msg = rawMsg && typeof rawMsg === 'object'
            ? Object.values(rawMsg).flat().join('\n')
            : (rawMsg || this.$t('orders.errorOccurredProcessingOrder'))
          swal(this.$t('common.error'), msg, 'error')
          this.loading = false
          this.clearProcessOrderData()
          stime.time = ''
          stime1.time = ''
          stime2.time = ''
        })
    },

    // Invoice methods
    onInvoiceCreated() {
      const orders = (this.generateInvoiceData && this.generateInvoiceData.orders) || []
      const orderIds = orders.map(o => o.order_id).filter(Boolean)

      if (orderIds.length === 0) {
        this.getResults()
        return
      }

      let token = localStorage.getItem('access_token')
      axios
        .put('api/user_delivering_multiple', { orders: orderIds }, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then(response => {
          const skipped = response.data && response.data.skipped_orders
          if (skipped && skipped.length > 0) {
            swal({
              title: this.$t('orders.someOrdersNotMarkedDelivered'),
              text: this.$t('orders.missingQrCodeForMessage', { list: skipped.join(', ') }),
              icon: 'warning'
            })
          }
          this.getResults()
        })
        .catch(err => {
          const msg = (err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : this.$t('orders.failedToMarkOrdersAsDelivered')
          swal(this.$t('common.warning'), msg, 'warning')
          this.getResults()
        })
    },

    onInvoiceViewed() {
      const orders = (this.generateInvoiceData && this.generateInvoiceData.orders) || []
      const orderIds = orders.map(o => o.order_id).filter(Boolean)
      if (orderIds.length === 0) return

      let token = localStorage.getItem('access_token')
      axios
        .put('api/user_delivering_multiple', { orders: orderIds }, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then(response => {
          const skipped = response.data && response.data.skipped_orders
          if (skipped && skipped.length > 0) {
            swal({
              title: this.$t('orders.someOrdersNotMarkedDelivered'),
              text: this.$t('orders.missingQrCodeForMessage', { list: skipped.join(', ') }),
              icon: 'warning'
            })
          }
        })
        .catch(err => {
          const msg = (err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : this.$t('orders.failedToMarkOrdersAsDelivered')
          swal(this.$t('common.warning'), msg, 'warning')
        })
    },

    openOrderInvoiceModal(r) {
      const openModal = () => {
        this.generateInvoiceData = {
          volume: 0,
          delivery_price: 0,
          local_delivery: Number(r.local_delivery) || 0,
          customer_id: r.user_id,
          customer_name: r.full_name,
          warehouse_id: r.warehouse_id_fk,
          transportation_type: r.transportation_type,
          is_ship_only: r.is_ship_only,
          phone: r.phone_number,
          orders: [{
            order_id: r.order_id,
            customer_name: r.full_name,
            phone: r.phone_number,
            customer_id: r.user_id,
            the_order_id: r.theOrderID,
            warehouse_id: r.warehouse_id_fk,
            transportation_type: r.transportation_type,
            is_ship_only: r.is_ship_only,
            total_amount: r.total_amount,
            product_name: r.product_name,
            size: r.size
          }]
        }
        this.orderInvoiceModal = true
      }

      // Same "already settled" warning as openOrderInvoiceFromHeader() — see its comment.
      if (r.payment_status === 'paid' || r.payment_status === 'partial_paid') {
        swal({
          title: this.$t('orders.orderAlreadyInvoicedTitle'),
          text: this.$t('changeOrderStatus.orderAlreadyHasInvoiceMessage', { orderId: r.theOrderID, status: this.paymentStatusLabel(r.payment_status), invoiceNumber: r.order_invoice_number ? ' (#' + r.order_invoice_number + ')' : '' }),
          icon: 'warning',
          buttons: true,
          dangerMode: true
        }).then((confirmed) => {
          if (confirmed) openModal()
        })
        return
      }

      openModal()
    },

    openOrderInvoiceFromHeader() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseAtLeastSelectOneOrder'),
          icon: 'warning',
          dangerMode: true
        })
        return
      }

      let customerIds = []
      let ordersArr = []
      // allOrder checkbox value format:
      // [0]=order_id, [1]=full_name, [2]=phone_number, [3]=user_id, [4]=theOrderID,
      // [5]=is_ship_only, [6]=product_name, [7]=warehouse_id_fk,
      // [8]=transportation_type, [9]=is_ship_only, [10]=total_amount, [11]=size,
      // [12]=payment_status, [13]=order_invoice_number
      this.qrCodeValues.forEach((x) => {
        customerIds.push(x[3])
        ordersArr.push({
          order_id: x[0],
          customer_name: x[1],
          phone: x[2],
          customer_id: x[3],
          the_order_id: x[4],
          warehouse_id: x[7],
          transportation_type: x[8],
          is_ship_only: x[9],
          total_amount: x[10],
          product_name: x[6],
          size: x[11],
          payment_status: x[12],
          order_invoice_number: x[13]
        })
      })

      if (this.checkDiff(customerIds)) {
        swal({
          text: this.$t('orders.selectedDifferentCustomers'),
          icon: 'warning',
          dangerMode: true
        })
        return
      }

      const openModal = () => {
        this.generateInvoiceData = {
          volume: 0,
          delivery_price: 0,
          local_delivery: 0,
          customer_id: ordersArr[0].customer_id,
          customer_name: ordersArr[0].customer_name,
          warehouse_id: ordersArr[0].warehouse_id,
          transportation_type: ordersArr[0].transportation_type,
          is_ship_only: ordersArr[0].is_ship_only,
          phone: ordersArr[0].phone,
          orders: ordersArr
        }

        this.orderInvoiceModal = true
      }

      // Orders can already sit on a paid/partially-paid invoice (approved, then invoiced and
      // settled earlier) — Generate Invoice on them again no longer double-charges the customer
      // (server-side, updateExistingInvoice() now caps any new payment to what's actually still
      // owed), but staff should still be told up front rather than being left to assume this is
      // fresh, unpaid work. List every such order so a multi-order selection doesn't hide which
      // ones are already settled.
      const alreadySettled = ordersArr.filter(o => o.payment_status === 'paid' || o.payment_status === 'partial_paid')

      if (alreadySettled.length > 0) {
        const lines = alreadySettled
          .map(o => `${o.the_order_id} — ${this.paymentStatusLabel(o.payment_status)}${o.order_invoice_number ? ' (invoice #' + o.order_invoice_number + ')' : ''}`)
          .join('\n')

        swal({
          title: this.$t('orders.someOrdersAlreadyInvoiced'),
          text: this.$t('changeOrderStatus.ordersAlreadyHaveInvoiceMessage', { lines }),
          icon: 'warning',
          buttons: true,
          dangerMode: true
        }).then((confirmed) => {
          if (confirmed) openModal()
        })
        return
      }

      openModal()
    },

    // Source button is commented out in the template for now (kept for later use).
    attachSelectedToDelivery() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseAtLeastSelectOneOrder'),
          icon: 'warning',
          dangerMode: true
        })
        return
      }

      const customerIds = this.qrCodeValues.map((x) => x[3])
      if (this.checkDiff(customerIds)) {
        swal({
          text: this.$t('orders.selectedDifferentCustomers'),
          icon: 'warning',
          dangerMode: true
        })
        return
      }

      const customerId = customerIds[0]
      const orderIds = this.qrCodeValues.map((x) => x[0])

      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('orders.confirmAttachToDeliveryRequest'),
        icon: 'warning',
        buttons: true
      }).then((willAttach) => {
        if (!willAttach) return

        let token = localStorage.getItem('access_token')
        axios
          .post('api/order-delivery/attach-orders', {
            customer_id: customerId,
            order_ids: orderIds
          }, { headers: { Authorization: 'Bearer ' + token } })
          .then((response) => {
            this.qrCodeValues = []
            this.getResults()
            swal({ text: response.data.message, icon: 'success' })
          })
          .catch((err) => {
            const msg = (err.response && err.response.data && err.response.data.message)
              ? err.response.data.message
              : 'Something went wrong!'
            swal({ text: msg, icon: 'error' })
          })
      })
    },

    moveToDamage(orderId) {
      swal({
        title: this.$t('orders.moveToDamageConfirmTitle'),
        text: this.$t('orders.confirmMoveToDamage'),
        icon: 'warning',
        buttons: true,
        dangerMode: true
      }).then((willMove) => {
        if (willMove) {
          let token = localStorage.getItem('access_token')
          axios
            .put(this.moveToDamageUrl + '/' + orderId, {}, { headers: { Authorization: 'Bearer ' + token } })
            .then((response) => {
              swal({ text: response.data.message, icon: 'success' })
              this.getResults()
            })
            .catch((error) => {
              swal({ text: this.$t('invoice.somethingWentWrong'), icon: 'error' })
            })
        }
      })
    },

    moveToReturn(orderId) {
      swal({
        title: this.$t('orders.moveToReturnConfirmTitle'),
        text: this.$t('orders.confirmMoveToReturn'),
        icon: 'warning',
        buttons: true,
        dangerMode: true
      }).then((willMove) => {
        if (willMove) {
          let token = localStorage.getItem('access_token')
          axios
            .put(this.moveToReturnUrl + '/' + orderId, {}, { headers: { Authorization: 'Bearer ' + token } })
            .then((response) => {
              swal({ text: response.data.message, icon: 'success' })
              this.getResults()
            })
            .catch((error) => {
              swal({ text: this.$t('invoice.somethingWentWrong'), icon: 'error' })
            })
        }
      })
    },

    checkDiff(arr) {
      return arr.some(val => val !== arr[0])
    },

    // Backed by the admin-configurable Order Status Settings page (statusHelpers
    // mixin) — bg is whatever color the admin picked for this status, with a
    // computed black/white text color for contrast.
    statusColor(status) {
      const bg = this.$_status_color(status)
      return { bg, color: this.$_readable_text_color(bg) }
    },

    statusLabel(status) {
      return this.$_status_label(status)
    },

    isStatusChangeLocked(order) {
      return this.lockCanceledOrderStatusChange && order.order_status === 'canceled'
    },

    paymentStatusColor(status, editCount) {
      // Auto-generated invoices (created automatically at order approval, never yet touched by
      // staff via Generate Invoice — see edit_count) show grey instead of the urgent red used for
      // a genuinely unpaid invoice, since there's nothing to chase yet. Matches
      // orderInvoices.vue's statusColor() convention.
      if (status === 'not_paid' && !Number(editCount)) {
        return { bg: '#E0E0E0', color: '#616161' }
      }

      const map = {
        not_paid: { bg: '#F63049', color: '#FFFFFF' },
        partial_paid: { bg: '#FFC85C', color: '#6B4C00' },
        paid: { bg: '#89D7B7', color: '#1A5940' },
        cancel: { bg: '#FB8C00', color: '#FFFFFF' }
      }
      return map[status] || { bg: '#F5F5F5', color: '#616161' }
    },

    paymentStatusLabel(status) {
      if (!status) return ''
      return status.replace(/_/g, ' ')
    },

    // null covers legacy invoices predating the office_status column — treated as
    // 'office' (light green), matching the office_status_filter query's same fallback
    // and orderInvoices.vue's own officeStatusColor()/officeStatusLabel().
    officeStatusColor(status) {
      return status === 'out_office'
        ? { bg: '#F63049', color: '#FFFFFF' }
        : { bg: '#89D7B7', color: '#1A5940' }
    },

    officeStatusLabel(status) {
      return status === 'out_office' ? this.$t('columns.outOffice') : this.$t('invoice.officeOption')
    },

    openChangeStatusDialog(order) {
      if (this.isStatusChangeLocked(order)) return
      this.changeStatusOrder = order
      this.changeStatusBulkMode = false
      this.selectedNewStatus = order.order_status
      this.changeStatusSaving = false

      const today = new Date().toISOString().split('T')[0]
      const d = new Date()
      d.setDate(d.getDate() + 7)
      const todayPlus7 = d.toISOString().split('T')[0]

      this.purchaseFields = {
        bank_id: this.kurdistanBankId,
        actual_price: order.total_amount || 0,
        barcode: order.barcode || order.theOrderID,
        warehouse_date: today,
        from_date: today,
        to_date: todayPlus7,
        purchase_note: ''
      }

      this.cancelFields = {
        selectedReason: null,
        cancel_reason: '',
        cancel_reason_ar: '',
        cancel_reason_ku: ''
      }

      this.refundFields = {
        selectedReason: null,
        refund_reason: '',
        refund_note: 'full refund',
        money_amount: order.total_amount || 0
      }

      this.changeStatusDialog = true
    },

    openBulkChangeStatusDialog() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseAtLeastSelectOneOrder'),
          icon: 'warning',
          dangerMode: true
        })
        return
      }

      this.changeStatusOrder = null
      this.changeStatusBulkMode = true
      this.selectedNewStatus = null
      this.changeStatusSaving = false

      const today = new Date().toISOString().split('T')[0]
      const d = new Date()
      d.setDate(d.getDate() + 7)
      const todayPlus7 = d.toISOString().split('T')[0]

      this.purchaseFields = {
        bank_id: this.kurdistanBankId,
        actual_price: 0,
        barcode: '',
        warehouse_date: today,
        from_date: today,
        to_date: todayPlus7,
        purchase_note: ''
      }

      this.cancelFields = {
        selectedReason: null,
        cancel_reason: '',
        cancel_reason_ar: '',
        cancel_reason_ku: ''
      }

      this.refundFields = {
        selectedReason: null,
        refund_reason: '',
        refund_note: 'full refund',
        money_amount: 0
      }

      this.changeStatusDialog = true
    },

    updateCancelReasonFields() {
      if (!this.cancelFields.selectedReason) return
      const reasonObj = this.reasons.find(r => r.value === this.cancelFields.selectedReason)
      if (reasonObj) {
        this.cancelFields.cancel_reason = reasonObj.value
        this.cancelFields.cancel_reason_ar = reasonObj.ar
        this.cancelFields.cancel_reason_ku = reasonObj.ku
      }
    },

    updateRefundReasonFields() {
      if (!this.refundFields.selectedReason) return
      const reasonObj = this.reasons.find(r => r.value === this.refundFields.selectedReason)
      if (reasonObj) {
        this.refundFields.refund_reason = reasonObj.value
      }
    },

    saveOrderStatus() {
      if (!this.selectedNewStatus) return
      if (this.changeStatusBulkMode) {
        this.saveBulkOrderStatus()
        return
      }
      if (!this.changeStatusOrder) return
      if (this.selectedNewStatus === this.changeStatusOrder.order_status) return
      const token = localStorage.getItem('access_token')
      this.changeStatusSaving = true

      let body = { status: this.selectedNewStatus }
      if (this.selectedNewStatus === 'purchased' && !this.isPureFallbackSelection) {
        body = Object.assign(body, this.purchaseFields)
      } else if (this.selectedNewStatus === 'canceled') {
        body = Object.assign(body, {
          cancel_reason: this.cancelFields.cancel_reason,
          cancel_reason_ar: this.cancelFields.cancel_reason_ar,
          cancel_reason_ku: this.cancelFields.cancel_reason_ku
        })
      } else if (this.selectedNewStatus === 'refunded') {
        body = Object.assign(body, {
          refund_reason: this.refundFields.refund_reason,
          refund_note: this.refundFields.refund_note,
          money_amount: this.refundFields.money_amount
        })
      }

      axios.put(
        `api/orders/${this.changeStatusOrder.order_id}/status`,
        body,
        { headers: { Authorization: 'Bearer ' + token } }
      ).then(() => {
        this.changeStatusOrder.order_status = this.selectedNewStatus
        this.changeStatusDialog = false
        this.getResults()
      }).catch(err => {
        const msg = (err.response && err.response.data && err.response.data.message)
          ? err.response.data.message
          : this.$t('changeOrderStatus.failedToChangeOrderStatus')
        swal(this.$t('common.error'), msg, 'error')
      }).finally(() => {
        this.changeStatusSaving = false
      })
    },

    saveBulkOrderStatus() {
      const token = localStorage.getItem('access_token')
      this.changeStatusSaving = true

      let body = {
        order_ids: this.qrCodeValues.map(x => x[0]),
        status: this.selectedNewStatus
      }
      if (this.selectedNewStatus === 'purchased' && !this.isPureFallbackSelection) {
        body = Object.assign(body, {
          bank_id: this.purchaseFields.bank_id,
          barcode: this.purchaseFields.barcode,
          warehouse_date: this.purchaseFields.warehouse_date,
          from_date: this.purchaseFields.from_date,
          to_date: this.purchaseFields.to_date,
          purchase_note: this.purchaseFields.purchase_note
        })
      } else if (this.selectedNewStatus === 'canceled') {
        body = Object.assign(body, {
          cancel_reason: this.cancelFields.cancel_reason,
          cancel_reason_ar: this.cancelFields.cancel_reason_ar,
          cancel_reason_ku: this.cancelFields.cancel_reason_ku
        })
      } else if (this.selectedNewStatus === 'refunded') {
        body = Object.assign(body, {
          refund_reason: this.refundFields.refund_reason,
          refund_note: this.refundFields.refund_note
        })
      }

      axios.put(
        'api/orders/change_status_multiple',
        body,
        { headers: { Authorization: 'Bearer ' + token } }
      ).then((response) => {
        this.changeStatusDialog = false
        this.qrCodeValues = []
        this.getResults()
        const msg = (response.data && response.data.message) ? response.data.message : this.$t('changeOrderStatus.ordersUpdatedSuccessfully')
        swal({ text: msg, icon: 'success', timer: 2000, buttons: false })
      }).catch(err => {
        const msg = (err.response && err.response.data && err.response.data.message)
          ? err.response.data.message
          : this.$t('changeOrderStatus.failedToChangeOrderStatus')
        swal(this.$t('common.error'), msg, 'error')
      }).finally(() => {
        this.changeStatusSaving = false
      })
    }
  }
}
</script>

<style>
/* This page alone has 7 extra filters (Warehouse/Status/Payment/Out Office/
   Date Type/Order Sort/Platform) on top of the shared Search/date-range/per-page/
   Show-Hide-Columns row, which is too many to stay on one line without
   looking cramped. This is an invisible, zero-height spacer placed right
   before Warehouse (not a class on Warehouse itself) - a 100%-wide flex
   item can't share a line with whatever came before it, so it forces a
   line break at that exact point, and Warehouse/Status/Payment/Out Office/
   Date Type/Order Sort/Platform all flow onto the new line together right after it,
   at their normal size. (Putting flex-basis:100% on Warehouse's own v-flex
   instead - what an earlier attempt did - forces WAREHOUSE to fill the
   entire new line by itself, pushing Status onto a third line alone too.) */
.filter-line-break {
  flex-basis: 100% !important;
  height: 0 !important;
  min-width: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}

.table > thead > tr > th, .table > tbody > tr > th, .table > tfoot > tr > th, .table > thead > tr > td, .table > tbody > tr > td, .table > tfoot > tr > td
{
  padding: 0 5px !important;
}

@media screen and (max-width: 600px) {
  th {
    font-weight: bold;
    color: #273246;
    padding-left: 5px !important;
  }

  td {
    padding-left: 5px !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}

.panel-heading h4 {
  text-transform: uppercase;
  margin-left: -20px;
  padding-left: 10px;
  font-weight: 500;
}

.col-xs-12 {
  padding-top: 10px;
  padding-bottom: 10px;
}
.overlay--active:before {
  opacity: 50 !important;
  background: rgba(0, 0, 0, 0.5) !important;
}

.dialog {
  box-shadow: 0 3px 5px -1px rgba(14, 14, 14, 0.212) !important;
  overflow: visible;
}
.page-stats {
  align-items: center;
  position: relative;
  top: -8px;
  margin-right: 5px;
}

a:hover {
  color: #03a9f4;
  position: relative;
}
a[tooltip]:hover:after {
  content: attr(tooltip);
  padding: 8px 12px;
  color: #ffffff;
  position: absolute;
  right: 80%;
  bottom: 150%;
  white-space: nowrap;
  z-index: 9999;
  -moz-border-radius: 3px;
  -webkit-border-radius: 3px;
  border-radius: 3px;
  -moz-box-shadow: 0px 0px 2px #484b4e;
  -webkit-box-shadow: 0px 0px 2px #484b4e;
  box-shadow: 0px 0px 2px #484b4e;
  background-color: #484b4e;
}

@media screen and (max-width: 767px) {
  .form-group {
    margin-top: 0;
    margin-bottom: 0;
  }
}
.customer-name-stack {
  display: inline-flex;
  flex-direction: column;
  vertical-align: middle;
}
.customer-phone-line {
  display: block;
  font-size: 14px;
  color: #666;
}
.combined-cell-td {
  vertical-align: middle;
}
.combined-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  font-size: 15px;
  line-height: 1.4;
  white-space: nowrap;
}
.combined-cell-label {
  color: #888;
}
.combined-cell-divider {
  width: 100%;
  border-top: 1px solid #ddd;
  margin: 3px 0;
}
</style>



// WEBPACK FOOTER //
// src/components/allOrder.vue